import { apiClient } from "../../lib/apiClient";
import type {
  ChatMessage,
  GuildBlocksResponse,
  GuildDetail,
  GuildFeedResponse,
  GuildsListResponse,
  HelpDonation,
  HelpDonationsResponse,
  UpdateGuildInput,
} from "./types";

export interface ListGuildsParams {
  q?: string;
  page?: number;
  limit?: number;
}

const base = (projectId: string) =>
  `/api/admin/projects/${encodeURIComponent(projectId)}/guilds`;

/** Fetch a page of guilds for a project. */
export async function listGuilds(
  projectId: string,
  params: ListGuildsParams = {},
): Promise<GuildsListResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.page) qs.set("page", String(params.page));
  if (params.limit) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const r = await apiClient.get<GuildsListResponse>(`${base(projectId)}${suffix}`);
  return r.data;
}

/** Fetch one guild with its members. */
export async function getGuild(
  projectId: string,
  guildId: string,
): Promise<GuildDetail> {
  const r = await apiClient.get<GuildDetail>(
    `${base(projectId)}/${encodeURIComponent(guildId)}`,
  );
  return r.data;
}

/** PATCH a guild's settings. Last-write-wins. Returns the updated detail. */
export async function updateGuild(
  projectId: string,
  guildId: string,
  input: UpdateGuildInput,
): Promise<GuildDetail> {
  const r = await apiClient.patch<GuildDetail>(
    `${base(projectId)}/${encodeURIComponent(guildId)}`,
    input,
  );
  return r.data;
}

/**
 * Disband a guild: its members, chat, invites, join requests, blocks and its
 * places in the leaderboard cycles still running all go with it. Answers 204
 * with no body — and 404 when the guild is already gone, which is also what a
 * repeated call gets.
 */
export async function deleteGuild(
  projectId: string,
  guildId: string,
): Promise<void> {
  await apiClient.delete(
    `${base(projectId)}/${encodeURIComponent(guildId)}`,
  );
}

/** Change a member's role. role: "leader"→transfer, "officer"→promote,
 *  "member"→demote. Returns the updated guild detail. */
export async function updateMemberRole(
  projectId: string,
  guildId: string,
  playerId: string,
  role: "leader" | "officer" | "member",
): Promise<GuildDetail> {
  const r = await apiClient.patch<GuildDetail>(
    `${base(projectId)}/${encodeURIComponent(guildId)}/members/${encodeURIComponent(playerId)}`,
    { role },
  );
  return r.data;
}

/** Kick a member from the guild. */
export async function kickMember(
  projectId: string,
  guildId: string,
  playerId: string,
): Promise<void> {
  await apiClient.delete(
    `${base(projectId)}/${encodeURIComponent(guildId)}/members/${encodeURIComponent(playerId)}`,
  );
}

/** Fetch one page of the players this guild has kicked and therefore blocked. */
export async function listBlocks(
  projectId: string,
  guildId: string,
  params: ListBlocksParams = {},
): Promise<GuildBlocksResponse> {
  const q = new URLSearchParams();
  if (params.page) q.set("page", String(params.page));
  if (params.limit) q.set("limit", String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : "";
  const r = await apiClient.get<GuildBlocksResponse>(
    `${base(projectId)}/${encodeURIComponent(guildId)}/blocks${suffix}`,
  );
  return r.data;
}

export interface ListBlocksParams {
  page?: number;
  limit?: number;
}

/** Lift a guild's block on a player, letting them join or apply again. */
export async function unblockPlayer(
  projectId: string,
  guildId: string,
  playerId: string,
): Promise<void> {
  await apiClient.delete(
    `${base(projectId)}/${encodeURIComponent(guildId)}/blocks/${encodeURIComponent(playerId)}`,
  );
}

export interface ListFeedParams {
  page?: number;
  limit?: number;
}

/**
 * Fetch a page of a guild's feed — chat messages and help requests merged into
 * one timeline, newest-first from the server. The route is still `/messages`:
 * posting and deleting messages go through the same one.
 */
export async function listFeed(
  projectId: string,
  guildId: string,
  params: ListFeedParams = {},
): Promise<GuildFeedResponse> {
  const qs = new URLSearchParams();
  if (params.page) qs.set("page", String(params.page));
  if (params.limit) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const r = await apiClient.get<GuildFeedResponse>(
    `${base(projectId)}/${encodeURIComponent(guildId)}/messages${suffix}`,
  );
  return r.data;
}

/** Fetch everyone who chipped in on one help request, oldest first. */
export async function listHelpDonations(
  projectId: string,
  guildId: string,
  helpId: string,
): Promise<HelpDonation[]> {
  const r = await apiClient.get<HelpDonationsResponse>(
    `${base(projectId)}/${encodeURIComponent(guildId)}/help-requests/${encodeURIComponent(helpId)}/donations`,
  );
  return r.data.items;
}

/** Post a system message to the guild chat as the admin. */
export async function postMessage(
  projectId: string,
  guildId: string,
  body: string,
): Promise<ChatMessage> {
  const r = await apiClient.post<ChatMessage>(
    `${base(projectId)}/${encodeURIComponent(guildId)}/messages`,
    { body },
  );
  return r.data;
}

/** Hard-delete one chat message. */
export async function deleteMessage(
  projectId: string,
  guildId: string,
  messageId: string,
): Promise<void> {
  await apiClient.delete(
    `${base(projectId)}/${encodeURIComponent(guildId)}/messages/${encodeURIComponent(messageId)}`,
  );
}
